import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import NewsAngBlogListItem from './NewsAngBlogListItem'
import NewsListFilter from './NewsListFilter'
import { NewsItem, NewsListProps } from 'Frontend/types/types'
import { MultiValue } from 'react-select'
import { ISelectOption } from 'Frontend/scripts/models/forums.model'
import { Form, Spinner } from 'react-bootstrap'
import Paging from 'Features/Paging/paging'
import useQueryParams from 'Frontend/hooks/useQueryParams'
import { useTranslation } from 'react-i18next'

type NewsCategory = { id: number; name: string }

const NewsList: FunctionComponent<NewsListProps> = ({
  hitsPerPage,
  showPaging,
  useMultiFilter,
  baseFilter,
  enableFilter,
}) => {
  const { getQueryParam } = useQueryParams()

  const [newsListItems, setNewsListItems] = useState<NewsItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<string>(getQueryParam('filters') ?? baseFilter)
  const [activePage, setActivePage] = useState<number>(
    getQueryParam('page') ? parseInt(getQueryParam('page') as string) : 1,
  )
  const [noOfPages, setNoOfPages] = useState<number>(1)
  const [newsCategories, setNewsCategories] = useState<ISelectOption[]>([])
  const { t } = useTranslation()

  const fetchNewsCategories = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`/api/news/getnewscategories`)
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`)
      const data = await response.json()

      const options: ISelectOption[] = data.map((cat: NewsCategory) => ({
        value: cat.id,
        label: cat.name,
      }))

      setNewsCategories(options)
    } catch {
      console.error('Failed to load news categories')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchNewsItems = useCallback(async () => {
    try {
      setIsLoading(true)
      let fetchUrl = `/api/news?page=${activePage}&pageSize=${hitsPerPage}`

      if (selectedFilters) fetchUrl += `&filters=${selectedFilters}`
      const response = await fetch(fetchUrl)

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`)
      const data = await response.json()
      setNoOfPages(data.numberOfPages)
      setNewsListItems(data.newsPosts)
    } catch {
      console.error('Failed to load listing data')
    } finally {
      setIsLoading(false)
    }
  }, [selectedFilters, activePage])

  const numberOfPages = useMemo(() => {
    return Math.ceil(noOfPages / hitsPerPage)
  }, [noOfPages])

  useEffect(() => {
    fetchNewsItems()
  }, [fetchNewsItems, selectedFilters])

  useEffect(() => {
    fetchNewsCategories()
  }, [])

  const onMultiFilterChanges = (filter: MultiValue<ISelectOption>) => {
    let filterArrary: string[] = []

    filter.map((option) => {
      filterArrary.push(option.value.toString())
    })

    setSelectedFilters(filterArrary.toString())
    setActivePage(1)
  }

  const onSingleFilterChanges = (filter: string) => {
    setSelectedFilters(filter)
    setActivePage(1)
  }

  const onPagingChanged = (newPage: number) => {
    setActivePage(newPage)
  }

  return (
    <>
      {enableFilter && (
        <>
          {useMultiFilter ? (
            <NewsListFilter
              callback={(selected: MultiValue<ISelectOption>) => onMultiFilterChanges(selected)}
              options={newsCategories}
            />
          ) : (
            <div className="newslist__filter mb-4">
              <span className="h3 fw-semibold d-block mb-0 mt-1">{t('filter-options')}</span>
              <Form.Group controlId="news-filter-select" className="newslist__container">
                <Form.Label>{t('category')}</Form.Label>
                <Form.Select
                  className="newslist__control "
                  value={selectedFilters}
                  onChange={(selected: ChangeEvent<HTMLSelectElement>) => onSingleFilterChanges(selected.target.value)}
                >
                  {newsCategories?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}
        </>
      )}
      {isLoading ? (
        <Spinner animation="border" role="status" className="mt-5" />
      ) : (
        <div className="newslist">
          {newsListItems?.map((item: NewsItem) => <NewsAngBlogListItem newsItem={item} key={item.contentLink.id} />)}
        </div>
      )}

      {showPaging && numberOfPages > 1 && (
        <Paging
          noOfPages={numberOfPages || 1}
          activePage={activePage || 1}
          callback={(selectedPage: number) => onPagingChanged(selectedPage)}
        ></Paging>
      )}
    </>
  )
}

export default NewsList
